import React, { useContext } from "react";
import { Drawer, Box, Typography, IconButton, Link, Fade } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isIOS, isAndroid } from "src/utils/platformUtils";
import styles from "../../content/pages/homepage.module.css";
import CloseIcon from "@mui/icons-material/Close";
import benchImage from "../../assets/bench.png";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { WidgetContext } from "src/contexts/WidgetContext";

interface RateLimitDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Timestamp (ms) after which the user can retry. Computed as next minute boundary. */
    retryAt?: number | null;
}

const RateLimitDrawer: React.FC<RateLimitDrawerProps> = ({ open, onClose, retryAt }) => {
    const isMobile = isMobileWebBrowser() || mobile;
    const { aiSettings } = useContext(WidgetContext);

    // Format retryAt as human-readable time with seconds (e.g. "2:34:05 PM")
    const retryAtFormatted = retryAt
        ? new Date(retryAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })
        : null;

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            hideBackdrop={false}
            sx={{ pointerEvents: "none" }}
            ModalProps={{
                keepMounted: true,
                sx: {
                    pointerEvents: "auto",
                    top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
                    zIndex: 1450,
                },
            }}
            PaperProps={{
                sx: {
                    pointerEvents: "auto",
                    width: {
                        xs: "100%",
                        sm: "720px",
                        md: "720px",
                        lg: "720px",
                      },
                      margin: {
                        xs: "0",
                        sm: "0 auto",
                        md: "0 auto",
                        lg: "0 auto",
                      },
                    //   height: isIOS || isAndroid
                    //     ? "calc(100svh - env(safe-area-inset-top) - 56px)"
                    //     : "calc(100vh - 56px)",
                    height: isIOS || isAndroid
                        ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
                        : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"),
                    border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    bgcolor: "#EDEDED",
                    zIndex: 1450,
                },
            }}
        >
            <Fade in={open}>
                <Box
                    className={styles.noScrollbar}
                    sx={{
                        bgcolor: "#ffffff",
                        opacity: open ? 1 : 0,
                        transition: "opacity 0.5s",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    {/* Header with title and close button - Fixed at top */}
                    <Box
                        sx={{
                            p: 1,
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexShrink: 0,
                            borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.black[100]}`,
                            px: 1,
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            bgcolor: "#EDEDED",
                        }}
                    >
                        <IconButton sx={{ visibility: "hidden" }}>
                            <CloseIcon />
                        </IconButton>
                        <Typography
                            variant="h4"
                            sx={{
                                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                fontSize: "16px",
                                flex: 1,
                                textAlign: "center",
                                fontWeight: "bold",
                            }}
                        >
                            Request Limit Reached
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={(e) => {
                                if (e.currentTarget) {
                                    e.currentTarget.blur();
                                }
                                onClose();
                            }}
                            sx={{
                                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                width: 40,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content area - Scrollable */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            "&::-webkit-scrollbar": { display: "none" },
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                pt: 0,
                                bgcolor: "#FFFFFF",
                                borderRadius: 0,
                            }}
                        >
                            {/* Bench image */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    overflow: "hidden",
                                    width: "100%",
                                    maxWidth: isMobile ? "250px" : "350px",
                                    height: isMobile ? "150px" : "200px",
                                    mt: -1,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={benchImage}
                                    alt="Bench illustration"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                    }}
                                />
                            </Box>

                            {/* Main title */}
                            <Typography
                                variant="h4"
                                sx={{
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "22px",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    pb: 1,
                                }}
                            >
                                Slow down, MVP!
                            </Typography>

                            {/* Description */}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "15px",
                                    textAlign: "center",
                                    fontWeight: "medium",
                                    pb: 1,
                                    px: 3,
                                }}
                            >
                                You've reached the maximum number of requests allowed per minute. Please wait a moment before trying again.
                            </Typography>

                            {/* Retry time */}
                            {retryAtFormatted && (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                        fontSize: "15px",
                                        textAlign: "center",
                                        fontWeight: "medium",
                                        pb: 2,
                                    }}
                                >
                                    You can try again at {retryAtFormatted}
                                </Typography>
                            )}

                            {/* Close link */}
                            <Link
                                component="button"
                                onClick={onClose}
                                sx={{
                                    pt: 1,
                                    color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                                    fontSize: "14px",
                                    textDecoration: "underline",
                                    textTransform: "none",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                }}
                            >
                                Got it, I'll wait
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </Fade>
        </Drawer>
    );
};

export default RateLimitDrawer;
